import React from "react";
import { Link } from "react-router-dom";
import { useBranding, brandingFallback } from "@/lib/branding";
import { Phone, Mail, MapPin, MessageCircle, ShieldCheck } from "lucide-react";

export default function Footer() {
    const branding = useBranding();
    const phone = branding.phone || "+1 (604) 337-5300";
    const phoneTel = phone.replace(/[^\d+]/g, "");
    const email = branding.email || "contact@cambridgecollegeonline.com";
    const address1 = branding.address || "Unit 109, 8166 128th Street, Surrey, BC, Canada V3W 1R1";
    const address2 = branding.address_line_2 || "Unit 280, 8128 128th Street, Surrey, BC, Canada V3W 4E6";

    return (
        <footer className="border-t border-border mt-24 bg-secondary/30" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-4 space-y-4">
                    <div className="flex items-start gap-3">
                        <img src={branding.logo_url} alt={branding.institution_name}
                             className="h-12 w-12 object-contain"
                             onError={brandingFallback} />
                        <div>
                            <div className="font-display text-primary text-lg leading-tight">
                                {branding.institution_name || "Cambridge College Online"}
                            </div>
                            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-1">
                                PTIB Registered · DLI Designated
                            </div>
                        </div>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
                        Career-focused, accredited online education delivered from British Columbia.
                        We empower students with practical skills and industry-recognized credentials.
                    </p>
                    <div className="flex items-center gap-2 pt-2 text-xs text-muted-foreground">
                        <ShieldCheck className="h-4 w-4 text-accent" />
                        <span>PTIB ID-01682 · DLI O19394940342</span>
                    </div>
                </div>

                <div className="md:col-span-2">
                    <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-4">
                        College
                    </div>
                    <ul className="space-y-3 text-sm text-foreground/80">
                        <li><Link to="/" className="hover:text-primary">Home</Link></li>
                        <li><Link to="/about" className="hover:text-primary">About Us</Link></li>
                        <li><Link to="/#catalog" className="hover:text-primary">Programs</Link></li>
                        <li><Link to="/contact" className="hover:text-primary">Contact</Link></li>
                        <li><Link to="/login" className="hover:text-primary">Student Portal</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-2">
                    <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-4">
                        Policies
                    </div>
                    <ul className="space-y-3 text-sm text-foreground/80">
                        <li><Link to="/policies/refund" className="hover:text-primary">Refund Policy</Link></li>
                        <li><Link to="/policies/privacy" className="hover:text-primary">Privacy</Link></li>
                        <li><Link to="/policies/terms" className="hover:text-primary">Terms of Use</Link></li>
                        <li><Link to="/policies/conduct" className="hover:text-primary">Student Conduct</Link></li>
                        <li><Link to="/policies/dispute" className="hover:text-primary">Dispute Resolution</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-4">
                    <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-4">
                        Contact
                    </div>
                    <ul className="space-y-3 text-sm text-foreground/80">
                        <li className="flex items-start gap-2">
                            <Phone className="h-4 w-4 mt-0.5 text-accent flex-shrink-0" />
                            <a href={`tel:${phoneTel}`} className="hover:text-primary">{phone}</a>
                        </li>
                        <li className="flex items-start gap-2">
                            <MessageCircle className="h-4 w-4 mt-0.5 text-accent flex-shrink-0" />
                            <a href={`https://api.whatsapp.com/send?phone=${phoneTel}`}
                               target="_blank" rel="noreferrer"
                               className="hover:text-primary">WhatsApp us</a>
                        </li>
                        <li className="flex items-start gap-2">
                            <Mail className="h-4 w-4 mt-0.5 text-accent flex-shrink-0" />
                            <a href={`mailto:${email}`} className="hover:text-primary">{email}</a>
                        </li>
                        <li className="flex items-start gap-2">
                            <MapPin className="h-4 w-4 mt-0.5 text-accent flex-shrink-0" />
                            <div className="leading-relaxed">
                                <div>{address1}</div>
                                {address2 && <div className="mt-1 text-muted-foreground">{address2}</div>}
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-border">
                <div className="max-w-7xl mx-auto px-6 py-5 text-xs text-muted-foreground flex flex-col md:flex-row items-center justify-between gap-2">
                    <span>© {new Date().getFullYear()} {branding.institution_name || "Cambridge College Online"}. All rights reserved.</span>
                    <span className="italic">{branding.tagline || "Your Gateway to Global Success"}</span>
                </div>
            </div>
        </footer>
    );
}
